import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { supabase } from '../lib/supabase';
import { useI18n } from '../lib/i18n';
import { useAgency } from '../lib/AgencyContext';
import Icon from './Icon';
import { StatusBadge, normalizeStatus, updateInvoiceStatus, PaymentDialog } from './InvoiceStatus';
import { fileToLogoDataUrl } from './logoUtil';
import SendInvoiceDialog from './SendInvoiceDialog';
import SefSendDialog, { sefLabel } from './SefSendDialog';
import { buildInvoicePdf } from './InvoicePdf';
import { formatRSD, formatCur, fmtDate } from '../lib/helpers';

// Naziv PDF fajla pri čuvanju (pregledač ga uzima iz naslova stranice)
// iPhone/iPad (i Safari na Mac-u sa dodirom): drugačije štampanje "zakucanog" podnožja
const IOS_PRINT = typeof navigator !== 'undefined' && (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
const FILE_KIND = { faktura: 'faktura', profaktura: 'profaktura', fiskalni: 'fiskalni račun' };
const DOC_TITLES = { faktura: 'FAKTURA', profaktura: 'PROFAKTURA', fiskalni: 'FISKALNI RAČUN' };

// ============================================================
// JEZIK DOKUMENTA — inostrana faktura može biti na srpskom, engleskom ili ruskom
// (bira se u formi fakture). Domaće fakture i fiskalni računi su uvek na srpskom.
// ============================================================
const DOC_TEXT = {
  sr: {
    title: { faktura: 'FAKTURA', profaktura: 'PROFAKTURA', fiskalni: 'FISKALNI RAČUN' },
    pib: 'PIB', mb: 'Matični broj', account: 'Tekući račun', iban: 'IBAN', swift: 'SWIFT/BIC', bank: 'Banka', tel: 'Tel', email: 'Email',
    issueDate: 'Datum fakture:', serviceDate: 'Datum prometa:', dueDate: 'Datum valute:', place: 'Mesto prometa:',
    forInvoice: 'Faktura za:', forProforma: 'Profaktura za:',
    custPibDom: 'PIB', custMbDom: 'Matični broj', custPibIno: 'Tax ID', custMbIno: 'Company registration number',
    colItem: 'Proizvod / usluga', colUnit: 'Jed. mere', colQty: 'Količina', colPrice: 'Cena', colDiscount: 'Popust', colTotal: 'Ukupno',
    noteTitle: 'Napomena:',
    vat: 'Nije u sistemu PDV-a. PDV nije obračunat na fakturi u skladu sa članom 33. Zakona o porezu na dodatu vrednost.',
    total: 'Ukupno:', totalDue: 'Ukupno za uplatu:',
    storno: 'Ovaj dokument je storniran.',
    proforma: 'Ovo je profaktura (predračun) i ne predstavlja poresku obavezu. Služi kao osnov za plaćanje pre izdavanja konačne fakture.',
    issuedBy: 'Fakturisao:', message: 'Poruka:',
    footer: 'Račun važi bez pečata i potpisa, u skladu sa članovima 8 i 9 Zakona o računovodstvu i članovima 7 i 10 Zakona o elektronskom dokumentu.',
    numLocale: 'sr-Latn-RS',
  },
  en: {
    title: { faktura: 'INVOICE', profaktura: 'PROFORMA INVOICE', fiskalni: 'FISCAL RECEIPT' },
    pib: 'Tax ID (PIB)', mb: 'Company reg. no. (MB)', account: 'Bank account', iban: 'IBAN', swift: 'SWIFT/BIC', bank: 'Bank', tel: 'Phone', email: 'Email',
    issueDate: 'Invoice date:', serviceDate: 'Date of supply:', dueDate: 'Due date:', place: 'Place of supply:',
    forInvoice: 'Bill to:', forProforma: 'Proforma for:',
    custPibDom: 'Tax ID', custMbDom: 'Company registration number', custPibIno: 'Tax ID', custMbIno: 'Company registration number',
    colItem: 'Product / service', colUnit: 'Unit', colQty: 'Quantity', colPrice: 'Price', colDiscount: 'Discount', colTotal: 'Total',
    noteTitle: 'Note:',
    vat: 'Not registered for VAT. VAT is not charged on this invoice in accordance with Article 33 of the Law on Value Added Tax of the Republic of Serbia.',
    total: 'Total:', totalDue: 'Amount due:',
    storno: 'This document has been cancelled.',
    proforma: 'This is a proforma invoice and does not constitute a tax liability. It serves as the basis for payment before the final invoice is issued.',
    issuedBy: 'Issued by:', message: 'Message:',
    footer: 'This invoice is valid without stamp and signature, in accordance with Articles 8 and 9 of the Law on Accounting and Articles 7 and 10 of the Law on Electronic Document of the Republic of Serbia.',
    numLocale: 'en-GB',
  },
  ru: {
    title: { faktura: 'СЧЁТ-ФАКТУРА', profaktura: 'СЧЁТ НА ПРЕДОПЛАТУ', fiskalni: 'ФИСКАЛЬНЫЙ ЧЕК' },
    pib: 'ИНН (PIB)', mb: 'Рег. номер (MB)', account: 'Расчётный счёт', iban: 'IBAN', swift: 'SWIFT/BIC', bank: 'Банк', tel: 'Тел.', email: 'Email',
    issueDate: 'Дата счёта:', serviceDate: 'Дата оказания услуг:', dueDate: 'Срок оплаты:', place: 'Место оказания:',
    forInvoice: 'Плательщик:', forProforma: 'Плательщик:',
    custPibDom: 'Налоговый номер', custMbDom: 'Регистрационный номер компании', custPibIno: 'Налоговый номер', custMbIno: 'Регистрационный номер компании',
    colItem: 'Товар / услуга', colUnit: 'Ед. изм.', colQty: 'Кол-во', colPrice: 'Цена', colDiscount: 'Скидка', colTotal: 'Сумма',
    noteTitle: 'Примечание:',
    vat: 'Не является плательщиком НДС. НДС не начислен в соответствии со статьёй 33 Закона о налоге на добавленную стоимость Республики Сербия.',
    total: 'Итого:', totalDue: 'Итого к оплате:',
    storno: 'Этот документ аннулирован.',
    proforma: 'Это счёт на предоплату, он не является налоговым обязательством. Служит основанием для оплаты до выставления окончательного счёта.',
    issuedBy: 'Счёт выставил:', message: 'Сообщение:',
    footer: 'Счёт действителен без печати и подписи в соответствии со статьями 8 и 9 Закона о бухгалтерском учёте и статьями 7 и 10 Закона об электронном документе Республики Сербия.',
    numLocale: 'ru-RU',
  },
};
// Jedinice mere iz menija (sopstvene jedinice ostaju kako su upisane)
const UNIT_TEXT = {
  en: { dan: 'Day', usluga: 'Service', komad: 'Piece', sat: 'Hour', mesec: 'Month', 'paušal': 'Flat fee', 'kilometar (km)': 'Kilometre (km)' },
  ru: { dan: 'День', usluga: 'Услуга', komad: 'Штука', sat: 'Час', mesec: 'Месяц', 'paušal': 'Фикс. оплата', 'kilometar (km)': 'Километр (км)', kg: 'кг', l: 'л', m: 'м', m2: 'м²', m3: 'м³' },
};
const unitIn = (unit, lang) => (UNIT_TEXT[lang] && UNIT_TEXT[lang][String(unit || '').trim().toLowerCase()]) || unit;

const num2 = (n) => new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

// Račun u formatu za NBS IPS QR: 18 cifara (banka 3 + partija 13 + kontrolni broj 2)
function ipsAccount(acc) {
  const parts = String(acc || '').split(/[^0-9]+/).filter(Boolean);
  let digits;
  if (parts.length === 3) digits = parts[0].padStart(3, '0') + parts[1].padStart(13, '0') + parts[2].padStart(2, '0');
  else digits = parts.join('');
  return /^\d{18}$/.test(digits) ? digits : null;
}

// Tekst NBS IPS QR koda (nalog za prenos) — https://ips.nbs.rs
function ipsPayload({ account, name, amount, purpose, reference }) {
  const clean = (s, max) => String(s || '').replace(/[|\r]/g, ' ').trim().slice(0, max);
  const parts = [
    'K:PR', 'V:01', 'C:1',
    `R:${account}`,
    `N:${clean(name, 70)}`,
    `I:RSD${num2(amount).replace(/\./g, '')}`,
    'SF:221',
    `S:${clean(purpose, 35)}`,
  ];
  if (reference) parts.push(`RO:00${reference.slice(0, 22)}`);
  return parts.join('|');
}

// Stavke fakture: nove fakture imaju listu stavki, starije jednu (opis + iznos)
function itemsOf(inv) {
  if (Array.isArray(inv.items) && inv.items.length > 0) {
    return inv.items.map((it) => {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.price) || 0;
      const disc = Number(it.discount) || 0;
      return { name: it.name, unit: it.unit || '', qty, price, discount: disc, total: qty * price * (1 - disc / 100) };
    });
  }
  return [{ name: inv.description, unit: 'usluga', qty: 1, price: Number(inv.amount) || 0, discount: 0, total: Number(inv.amount) || 0 }];
}

export default function InvoicePrintView() {
  const { id } = useParams();
  const { tr } = useI18n();
  const L = tr;
  const navigate = useNavigate();
  const { refreshYears, refreshProfile } = useAgency();
  const [logoError, setLogoError] = useState('');
  const [logoBusy, setLogoBusy] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [issuer, setIssuer] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dijalozi: brisanje (2 provere), storno, unos uplate
  const [dialog, setDialog] = useState(null); // null | 'delete1' | 'delete2' | 'storno' | 'payment'
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [sending, setSending] = useState(false); // prozor "Pošalji fakturu"
  const [sefOpen, setSefOpen] = useState(false); // prozor "Pošalji u SEF"
  // Profaktura: faktura napravljena od nje (nestornirana), ako postoji
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  useEffect(() => {
    if (!invoice || invoice.document_kind !== 'profaktura') { setLinkedInvoice(null); return; }
    let cancelled = false;
    supabase.from('invoices').select('id, invoice_number')
      .eq('source_proforma_id', invoice.id).or('status.is.null,status.neq.stornirano')
      .limit(1).maybeSingle()
      .then(({ data }) => { if (!cancelled) setLinkedInvoice(data || null); });
    return () => { cancelled = true; };
  }, [invoice]);

  // Telefon: prvo pregled (iznos, status, podaci), a prava faktura (A4) tek na dugme
  const [showDoc, setShowDoc] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [docZoom, setDocZoom] = useState(1);
  // Radnja iz menija ⋮ u listi faktura: ?akcija=pdf | storno | obrisi | uplata
  const location = useLocation();
  useEffect(() => {
    if (loading || !invoice) return;
    const akcija = new URLSearchParams(location.search).get('akcija');
    if (!akcija) return;
    navigate(location.pathname, { replace: true }); // očisti adresu, da se radnja ne ponovi
    setDialogError('');
    if (akcija === 'storno') setDialog('storno');
    else if (akcija === 'obrisi') setDialog('delete1');
    else if (akcija === 'uplata') setDialog('payment');
    else if (akcija === 'pdf') setTimeout(() => window.print(), 500);
    else if (akcija === 'posalji') setSending(true);
    else if (akcija === 'sef') setSefOpen(true);
  }, [loading, invoice, location.search, location.pathname, navigate]);

  useEffect(() => {
    const fit = () => setDocZoom(Math.min(1, (window.innerWidth - 48) / 820));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  useEffect(() => {
    (async () => {
      const { data: inv } = await supabase.from('invoices').select('*').eq('id', id).single();
      if (inv) {
        setInvoice(inv);
        // Izdavalac: stare fakture vezane za klijenta (actor) ostaju kako su izdate;
        // nove fakture koriste podatke agencije (stranica Agencija).
        let iss = null;
        if (inv.actor_id) {
          const { data: a } = await supabase.from('actors').select('*').eq('id', inv.actor_id).maybeSingle();
          if (a) iss = a;
        }
        if (!iss && inv.agency_id) {
          const { data: p } = await supabase.from('agency_profile').select('*').eq('agency_id', inv.agency_id).maybeSingle();
          if (p) {
            iss = {
              fromProfile: true,
              name: p.company_name,
              shortName: p.short_name,
              ownerName: p.owner_name,
              nameDisplay: p.name_display,
              streetLine: [p.street, p.street_number].filter(Boolean).join(' '),
              cityLine: [p.postal_code, p.city].filter(Boolean).join(' '),
              pib: p.pib, mb: p.mb, phone: p.phone, email: p.email,
              account_dom: p.account_dom, account_ino: p.account_ino,
              iban_bank: p.iban_bank, swift: p.swift, logo: p.logo_data,
            };
            iss.address = [iss.streetLine, iss.cityLine].filter(Boolean).join(', ');
          }
        }
        setIssuer(iss || { name: '' });
        if (inv.customer_id) {
          const { data: c } = await supabase.from('customers').select('*').eq('id', inv.customer_id).maybeSingle();
          setCustomer(c || null);
        }
      }
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (!invoice) return;
    const prevTitle = document.title;
    const kind = FILE_KIND[invoice.document_kind] || 'faktura';
    const n = (invoice.invoice_number || '').trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ');
    // Naziv fajla po izdavaocu (pun ili skraćeni naziv, kako je izabrano za fakturu)
    const mode = invoice.issuer_name_mode || issuer?.nameDisplay || 'full';
    const who = String((mode === 'short' && issuer?.shortName ? issuer.shortName : issuer?.name) || 'Faktura')
      .replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
    document.title = `${who} ${kind}${n ? ' ' + n : ''}`;
    return () => { document.title = prevTitle; };
  }, [invoice, issuer]);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'print-style';
    style.innerHTML = `
      @media screen { #invoice-print-portal { display: none; } }
      @media print {
        body > *:not(#invoice-print-portal) { display: none !important; }
        #invoice-print-portal { display: block !important; }
        #invoice-print-portal > div {
          box-shadow: none !important; margin: 0 !important; padding: 14mm !important;
          max-width: none !important; border-radius: 0 !important; border: none !important;
          box-sizing: border-box !important; padding: 12mm !important;
          /* Bez zadate visine: sadržaj ide odozgo, a tekst o važenju računa je zakucan na dno strane (ispod) */
          min-height: 0 !important; display: block !important;
        }
        /* Tekst o važenju računa: uvek zakucan na dno A4 strane (isto na računaru i telefonu,
           bez obzira na margine pregledača i dužinu fakture) */
        #invoice-print-portal .invoice-legal-footer {
          position: fixed !important; left: 12mm !important; right: 12mm !important; bottom: 8mm !important;
          margin: 0 !important; background: #fff;
        }
        html, body { margin: 0 !important; padding: 0 !important; height: auto !important; }
        #invoice-print-portal > div { break-after: avoid; page-break-after: avoid; line-height: 1.5 !important; padding: 10mm 12mm 26mm !important; }
        /* Zbijeniji razmaci samo za štampu/PDF, da cela faktura stane na jednu stranu */
        #invoice-print-portal .pv-divider { margin: 12px 0 !important; }
        #invoice-print-portal .pv-items { margin-top: 16px !important; }
        #invoice-print-portal .pv-items th { padding: 8px 8px !important; }
        #invoice-print-portal .pv-items td { padding: 8px 8px !important; }
        #invoice-print-portal .pv-totals { margin-top: 10px !important; }
        #invoice-print-portal .pv-bottom { margin-top: 16px !important; margin-bottom: 12px !important; padding-top: 12px !important; }
        #invoice-print-portal .pv-logo { margin-bottom: 6px !important; }
        #invoice-print-portal table, #invoice-print-portal tr { break-inside: avoid; page-break-inside: avoid; }
        #invoice-print-portal .no-print-doc { display: none !important; }
        /* Profaktura: napomena zbijenija, da sve stane na jednu stranu */
        #invoice-print-portal .pv-proforma-note { margin-top: 10px !important; padding: 7px 10px !important; font-size: 10.5px !important; break-inside: avoid; }
        /* iPhone / iPad (Safari): "zakucan" tekst na dnu se tamo pogrešno postavlja preko sadržaja,
           pa na tim uređajima tekst o važenju računa ide normalno, odmah ispod sadržaja */
        #invoice-print-portal.print-flow .invoice-legal-footer { position: static !important; margin-top: 16px !important; }
        #invoice-print-portal.print-flow > div { padding: 10mm 12mm 10mm !important; }
        #invoice-print-portal, #invoice-print-portal * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
      @page { size: A4; margin: 0; }
    `;
    document.head.appendChild(style);
    return () => { const s = document.getElementById('print-style'); if (s) s.remove(); };
  }, []);

  if (loading) return <div style={styles.center}>{L('Učitavanje...', 'Loading...')}</div>;
  if (!invoice) return <div style={styles.center}>{L('Dokument nije pronađen.', 'Document not found.')}</div>;

  const st = normalizeStatus(invoice.status);
  const kind = invoice.document_kind;
  const isRsd = invoice.type === 'domaca' || kind === 'fiskalni';
  const cur = isRsd ? 'RSD' : invoice.currency;
  const money = (n) => (isRsd ? formatRSD(n) : formatCur(n, cur));
  // Jezik dokumenta: samo inostrane fakture/profakture mogu biti na engleskom ili ruskom
  const dl = !isRsd && ['en', 'ru'].includes(invoice.doc_lang) ? invoice.doc_lang : 'sr';
  const D = DOC_TEXT[dl];
  const items = itemsOf(invoice);
  const hasDiscount = items.some((i) => i.discount > 0);
  const total = Number(invoice.amount) || 0;
  const paid = st === 'naplaceno' ? total : st === 'delimicno' ? Math.min(Number(invoice.paid_amount) || 0, total) : 0;
  const due = st === 'stornirano' ? 0 : Math.max(total - paid, 0); // preostalo (za unos uplate u aplikaciji)
  // Na fakturi (dokument za klijenta) uvek pun iznos; naplata se prati samo kroz status u aplikaciji
  const printDue = st === 'stornirano' ? 0 : total;

  // QR: samo za domaće fakture u dinarima, sa ispravnim brojem računa i iznosom za uplatu
  const qrAccount = isRsd && kind !== 'fiskalni' ? ipsAccount(issuer.account_dom) : null;
  const qrValue = qrAccount && printDue > 0
    ? ipsPayload({
      account: qrAccount,
      name: [issuer.name, issuer.streetLine, issuer.cityLine].filter(Boolean).join(', '),
      amount: printDue,
      purpose: `Placanje po fakturi ${invoice.invoice_number || ''}`,
      reference: String(invoice.invoice_number || '').replace(/\D/g, ''),
    })
    : null;

  // Pun ili skraćeni naziv: izbor sa fakture, inače podrazumevani sa stranice Agencija
  const nameMode = invoice.issuer_name_mode || issuer.nameDisplay || 'full';
  const issuerName = nameMode === 'short' && issuer.shortName ? issuer.shortName : issuer.name;

  const uploadLogo = async (file) => {
    setLogoError('');
    try {
      const dataUrl = await fileToLogoDataUrl(file);
      setLogoBusy(true);
      const { error } = await supabase.from('agency_profile').update({ logo_data: dataUrl }).eq('agency_id', invoice.agency_id);
      setLogoBusy(false);
      if (error) { setLogoError(error.message); return; }
      setIssuer((iss) => ({ ...iss, logo: dataUrl }));
      if (refreshProfile) refreshProfile();
    } catch (err) {
      setLogoBusy(false);
      setLogoError(err.message);
    }
  };

  // Fakturisao: uvek osoba (ime i prezime obveznika), bez "PR" i naziva radnje
  const signer = (issuer.ownerName || '').trim()
    || String(issuer.name || '').split(/\s+PR(\s|$)/i)[0].trim();

  const custAddress1 = customer?.address || '';
  const custAddress2 = [[customer?.postal_code, customer?.city].filter(Boolean).join(' '), customer?.is_foreign ? customer?.country : '']
    .filter(Boolean).join(', ');

  // ---------- akcije ----------
  const openDialog = (d) => { setDialogError(''); setDialog(d); if (d === 'payment') setPayAmount(due ? String(due) : ''); };
  const closeDialog = () => { if (!busy) setDialog(null); };

  const doDelete = async () => {
    setBusy(true);
    const { error } = await supabase.from('invoices').delete().eq('id', id);
    setBusy(false);
    if (error) { setDialogError(error.message); return; }
    if (refreshYears) refreshYears();
    navigate('/fakturisanje', { replace: true });
  };

  const doStorno = async () => {
    setBusy(true);
    const { data, error } = await updateInvoiceStatus(invoice, 'stornirano');
    setBusy(false);
    if (error) { setDialogError(error.message); return; }
    setInvoice(data);
    setDialog(null);
  };

  const doPayment = async () => {
    const v = parseFloat(String(payAmount).replace(',', '.'));
    if (!(v > 0)) { setDialogError(L('Unesite iznos uplate.', 'Enter the payment amount.')); return; }
    const newPaid = paid + v;
    setBusy(true);
    const { data, error } = newPaid >= total - 0.004
      ? await updateInvoiceStatus(invoice, 'naplaceno')
      : await updateInvoiceStatus(invoice, 'delimicno', Math.round(newPaid * 100) / 100);
    setBusy(false);
    if (error) { setDialogError(error.message); return; }
    setInvoice(data);
    setDialog(null);
  };

  const docEl = (
    <div style={styles.doc}>
      {/* Zaglavlje: izdavalac levo, dokument i datumi desno */}
      <div style={styles.topRow}>
        <div style={{ flex: 1, minWidth: 240, ...styles.headerText }}>
          {issuer.logo && <img src={issuer.logo} alt="" style={styles.logo} className="pv-logo" />}
          {!issuer.logo && issuer.fromProfile && (
            <label
              className="no-print-doc"
              style={styles.logoDrop}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); uploadLogo(e.dataTransfer.files?.[0]); }}
            >
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: 'none' }}
                onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }} />
              <Icon name="download" size={20} color="var(--lavender-700)" style={{ transform: 'rotate(180deg)' }} />
              <span style={{ fontWeight: 700 }}>{logoBusy ? 'Čuvanje...' : 'Dodajte logo (nije obavezno)'}</span>
              <span style={{ fontSize: 11, color: '#5f6b71' }}>Prevucite sliku ovde ili kliknite da je izaberete</span>
              {logoError && <span style={{ fontSize: 11, color: '#a12828' }}>{logoError}</span>}
            </label>
          )}
          <div style={styles.issuerName}>{issuerName}</div>
          {issuer.agency && <div>{issuer.agency}</div>}
          {issuer.pib && <div>{D.pib}: {issuer.pib}</div>}
          {issuer.mb && <div>{D.mb}: {issuer.mb}</div>}
          {kind !== 'fiskalni' && isRsd && issuer.account_dom && <div>{D.account}: <strong>{issuer.account_dom}</strong></div>}
          {kind !== 'fiskalni' && !isRsd && issuer.account_ino && <div>{D.iban}: <strong>{issuer.account_ino}</strong></div>}
          {kind !== 'fiskalni' && !isRsd && issuer.swift && <div>{D.swift}: <strong>{issuer.swift}</strong></div>}
          {kind !== 'fiskalni' && !isRsd && issuer.iban_bank && <div>{D.bank}: {issuer.iban_bank}</div>}
          {issuer.address && <div>{issuer.address}</div>}
          {issuer.phone && <div>{D.tel}: {issuer.phone}</div>}
          {issuer.email && <div>{D.email}: {issuer.email}</div>}
        </div>
        <div style={{ ...styles.metaBox, ...styles.headerText }}>
          <div style={styles.docTitle}>{D.title[kind] || D.title.faktura}</div>
          {invoice.invoice_number && <div style={styles.docNumber}>{invoice.invoice_number}</div>}
          <div className="no-print-doc" style={{ margin: '8px 0 12px' }}>{kind !== 'profaktura' && <StatusBadge status={invoice.status} />}</div>
          <table style={styles.metaTable}>
            <tbody>
              <tr><td style={styles.metaLabel}>{D.issueDate}</td><td style={styles.metaValue}>{fmtDate(invoice.issue_date)}</td></tr>
              <tr><td style={styles.metaLabel}>{D.serviceDate}</td><td style={styles.metaValue}>{fmtDate(invoice.service_date)}</td></tr>
              {invoice.due_date && <tr><td style={styles.metaLabel}>{D.dueDate}</td><td style={styles.metaValue}>{fmtDate(invoice.due_date)}</td></tr>}
              {invoice.place_of_issue && <tr><td style={styles.metaLabel}>{D.place}</td><td style={styles.metaValue}>{invoice.place_of_issue}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div style={styles.divider} className="pv-divider" />

      {/* Kupac i podaci za uplatu */}
      <div style={styles.topRow}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={styles.blockLabel}>{kind === 'profaktura' ? D.forProforma : D.forInvoice}</div>
          <div style={styles.partyName}>{customer?.name || invoice.client || '—'}</div>
          {custAddress1 && <div>{custAddress1}</div>}
          {custAddress2 && <div>{custAddress2}</div>}
          {/* Inostrani kupac: nazivi na engleskom, jer brojevi nisu srpski PIB i MB */}
          {customer?.pib && <div>{customer.is_foreign ? D.custPibIno : D.custPibDom}: {customer.pib}</div>}
          {customer?.mb && <div>{customer.is_foreign ? D.custMbIno : D.custMbDom}: {customer.mb}</div>}
        </div>
      </div>

      {/* Stavke */}
      <table style={styles.items} className="pv-items">
        <thead>
          <tr>
            <th style={styles.th}>{D.colItem}</th>
            <th style={styles.th}>{D.colUnit}</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>{D.colQty}</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>{D.colPrice}</th>
            {hasDiscount && <th style={{ ...styles.th, textAlign: 'right' }}>{D.colDiscount}</th>}
            <th style={{ ...styles.th, textAlign: 'right' }}>{D.colTotal}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td style={styles.td}>{it.name}</td>
              <td style={styles.td}>{unitIn(it.unit, dl)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{it.qty.toLocaleString('sr-Latn-RS')}</td>
              <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{num2(it.price)}</td>
              {hasDiscount && <td style={{ ...styles.td, textAlign: 'right' }}>{it.discount ? `${it.discount}%` : '—'}</td>}
              <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{money(it.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={styles.totalsRow} className="pv-totals">
        <div style={styles.vatNote}>
          <div style={{ fontWeight: 800, marginBottom: 2 }}>{D.noteTitle}</div>
          <div>{D.vat}</div>
        </div>
      <div style={styles.totals}>
        <div style={styles.totalRow}><span>{D.total}</span><strong>{money(total)}</strong></div>
        <div style={{ ...styles.totalRow, ...styles.totalDue }}><span>{D.totalDue}</span><span>{money(printDue)}</span></div>
      </div>
      </div>

      {st === 'stornirano' && <div style={styles.stornoNote}>{D.storno}</div>}
      {kind === 'profaktura' && (
        <div style={styles.proformaNote} className="pv-proforma-note">{D.proforma}</div>
      )}

      {/* Fakturisao, poruka i QR */}
      <div style={styles.bottomRow} className="pv-bottom">
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={styles.signLabel}>{D.issuedBy}</div>
          <div style={styles.signName}>{signer || '—'}</div>
          {invoice.note && (
            <div style={{ marginTop: 18 }}>
              <div style={styles.blockLabel}>{D.message}</div>
              <p style={styles.note}>{invoice.note}</p>
            </div>
          )}
        </div>
        {qrValue && (
          <div style={styles.qrBox}>
            <QRCodeSVG value={qrValue} size={124} level="M" />
            <div style={styles.qrLabel}>NBS IPS QR</div>
          </div>
        )}
      </div>

      <div style={styles.footer} className="invoice-legal-footer">
        {D.footer}
      </div>
    </div>
  );

  const Btn = ({ icon, label, onClick, variant = 'outline' }) => (
    <button onClick={onClick} style={{ ...styles.btn, ...styles[variant] }}>
      <Icon name={icon} size={16} />
      {label}
    </button>
  );

  // ---------- telefon: pregled fakture ----------
  const kindLabel = { faktura: L('Faktura', 'Invoice', 'Счёт'), profaktura: L('Profaktura', 'Proforma', 'Проформа'), fiskalni: L('Fiskalni račun', 'Fiscal receipt', 'Фискальный чек') }[kind] || L('Faktura', 'Invoice', 'Счёт');
  const canPay = kind !== 'profaktura' && st !== 'stornirano' && st !== 'naplaceno';
  const canStorno = kind !== 'profaktura' && st !== 'stornirano';
  const rate = Number(invoice.exchange_rate) || 0;
  const infoRows = [
    [L('Datum fakture', 'Invoice date', 'Дата счёта'), fmtDate(invoice.issue_date)],
    [L('Datum prometa', 'Date of supply', 'Дата оказания'), fmtDate(invoice.service_date)],
    invoice.due_date && [L('Rok plaćanja', 'Due date', 'Срок оплаты'), fmtDate(invoice.due_date)],
    invoice.place_of_issue && [L('Mesto prometa', 'Place of supply', 'Место оказания'), invoice.place_of_issue],
    customer?.pib && [customer.is_foreign ? 'Tax ID' : L('PIB klijenta', 'Client tax ID', 'ИНН клиента'), customer.pib],
    !isRsd && rate > 0 && [L('U dinarima (kurs NBS)', 'In RSD (NBS rate)', 'В динарах (курс НБС)'), formatRSD(total * rate)],
    st === 'delimicno' && [L('Plaćeno', 'Paid', 'Оплачено'), money(paid)],
    st === 'delimicno' && [L('Preostalo', 'Remaining', 'Осталось'), money(due)],
    invoice.sent_at && [L('Poslato mejlom', 'Sent by e-mail', 'Отправлено'), `${fmtDate(String(invoice.sent_at).slice(0, 10))} · ${invoice.sent_to || ''}`],
  ].filter(Boolean);

  const menuAction = (fn) => { setMoreOpen(false); fn(); };

  // ---------- pravi PDF (tekst) za prilog uz mejl — isti podaci kao na fakturi ----------
  const buildPdf = () => {
    const issuerLines = [
      issuer.agency && { value: issuer.agency },
      issuer.pib && { label: D.pib, value: issuer.pib },
      issuer.mb && { label: D.mb, value: issuer.mb },
      kind !== 'fiskalni' && isRsd && issuer.account_dom && { label: D.account, value: issuer.account_dom, bold: true },
      kind !== 'fiskalni' && !isRsd && issuer.account_ino && { label: D.iban, value: issuer.account_ino, bold: true },
      kind !== 'fiskalni' && !isRsd && issuer.swift && { label: D.swift, value: issuer.swift, bold: true },
      kind !== 'fiskalni' && !isRsd && issuer.iban_bank && { label: D.bank, value: issuer.iban_bank },
      issuer.address && { value: issuer.address },
      issuer.phone && { label: D.tel, value: issuer.phone },
      issuer.email && { label: D.email, value: issuer.email },
    ].filter(Boolean);
    const meta = [
      [D.issueDate, fmtDate(invoice.issue_date)],
      [D.serviceDate, fmtDate(invoice.service_date)],
      invoice.due_date && [D.dueDate, fmtDate(invoice.due_date)],
      invoice.place_of_issue && [D.place, invoice.place_of_issue],
    ].filter(Boolean);
    const customerLines = [
      custAddress1, custAddress2,
      customer?.pib && `${customer.is_foreign ? D.custPibIno : D.custPibDom}: ${customer.pib}`,
      customer?.mb && `${customer.is_foreign ? D.custMbIno : D.custMbDom}: ${customer.mb}`,
    ].filter(Boolean);
    const qrCanvas = document.getElementById('ips-qr-canvas');
    return buildInvoicePdf({
      lang: dl,
      fileTitle: document.title,
      title: D.title[kind] || D.title.faktura,
      number: invoice.invoice_number || '',
      logo: issuer.logo || null,
      issuerName,
      issuerLines,
      meta,
      forLabel: kind === 'profaktura' ? D.forProforma : D.forInvoice,
      customerName: customer?.name || invoice.client || '—',
      customerLines,
      cols: { item: D.colItem, unit: D.colUnit, qty: D.colQty, price: D.colPrice, discount: D.colDiscount, total: D.colTotal },
      hasDiscount,
      items: items.map((it) => ({
        name: it.name,
        unit: unitIn(it.unit, dl) || '',
        qty: it.qty.toLocaleString('sr-Latn-RS'),
        price: num2(it.price),
        discount: it.discount ? `${it.discount}%` : '—',
        total: money(it.total),
      })),
      noteTitle: D.noteTitle,
      vat: D.vat,
      totalLabel: D.total,
      totalValue: money(total),
      dueLabel: D.totalDue,
      dueValue: money(printDue),
      stornoText: st === 'stornirano' ? D.storno : '',
      proformaText: kind === 'profaktura' ? D.proforma : '',
      issuedByLabel: D.issuedBy,
      signer,
      messageLabel: D.message,
      message: invoice.note || '',
      qrDataUrl: qrValue && qrCanvas ? qrCanvas.toDataURL('image/png') : null,
      footer: D.footer,
    });
  };

  const mobileEl = showDoc ? (
    <div className="no-print ipv-mobile">
      <div style={styles.mHeader}>
        <button onClick={() => setShowDoc(false)} style={styles.mRound} aria-label={L('Nazad na pregled', 'Back to summary', 'Назад')}>
          <Icon name="arrowLeft" size={18} />
        </button>
        <span style={styles.mTitle}>{kindLabel} {invoice.invoice_number}</span>
        <button onClick={() => window.print()} style={styles.mPdfSmall}>
          <Icon name="download" size={16} />PDF
        </button>
      </div>
      <div style={{ width: 820, zoom: docZoom }} className="ipv-client-view">{docEl}</div>
    </div>
  ) : (
    <div className="no-print ipv-mobile">
      <div style={styles.mHeader}>
        <button onClick={() => navigate('/fakturisanje')} style={styles.mRound} aria-label={L('Nazad na listu', 'Back to list', 'Назад к списку')}>
          <Icon name="arrowLeft" size={18} />
        </button>
        <span style={styles.mTitle}>{kindLabel} {invoice.invoice_number}</span>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setMoreOpen((o) => !o)} style={styles.mRound} aria-label={L('Još opcija', 'More options', 'Ещё')} aria-expanded={moreOpen}>
            <Icon name="moreVertical" size={18} />
          </button>
          {moreOpen && (
            <>
              <div style={styles.mBackdrop} onClick={() => setMoreOpen(false)} />
              <div style={styles.mMenu} role="menu">
                <button role="menuitem" style={styles.mMenuItem} onClick={() => menuAction(() => setSending(true))}>
                  <Icon name="logout" size={16} />{L('Pošalji mejlom', 'Send by e-mail', 'Отправить')}
                </button>
                {(kind || 'faktura') === 'faktura' && !invoice.sef_invoice_id && st !== 'stornirano' && (
                  <button role="menuitem" style={styles.mMenuItem} onClick={() => menuAction(() => setSefOpen(true))}>
                    <Icon name="globe" size={16} />{L('Pošalji u SEF (eFaktura)', 'Send to SEF', 'Отправить в SEF')}
                  </button>
                )}
                {kind === 'profaktura' && (
                  <button role="menuitem" style={styles.mMenuItem} onClick={() => menuAction(() => navigate(linkedInvoice ? `/fakturisanje/${linkedInvoice.id}/pregled` : `/fakturisanje/nova?iz-profakture=${id}`))}>
                    <Icon name="receipt" size={16} />{linkedInvoice ? L(`Otvori fakturu ${linkedInvoice.invoice_number || ''}`, `Open invoice ${linkedInvoice.invoice_number || ''}`, `Открыть счёт ${linkedInvoice.invoice_number || ''}`) : L('Napravi fakturu', 'Create invoice', 'Создать счёт')}
                  </button>
                )}
                <button role="menuitem" style={styles.mMenuItem} onClick={() => menuAction(() => navigate(`/fakturisanje/${id}/izmeni`))}>
                  <Icon name="pencil" size={16} />{L('Izmeni', 'Edit', 'Изменить')}
                </button>
                <button role="menuitem" style={styles.mMenuItem} onClick={() => menuAction(() => navigate(`/fakturisanje/nova?kopija=${id}`))}>
                  <Icon name="fileText" size={16} />{L('Napravi kopiju', 'Duplicate', 'Копировать')}
                </button>
                {canStorno && (
                  <button role="menuitem" style={{ ...styles.mMenuItem, color: 'var(--red-600)' }} onClick={() => menuAction(() => openDialog('storno'))}>
                    <Icon name="x" size={16} />{L('Storniraj', 'Cancel invoice', 'Аннулировать')}
                  </button>
                )}
                <button role="menuitem" style={{ ...styles.mMenuItem, color: 'var(--red-600)' }} onClick={() => menuAction(() => openDialog('delete1'))}>
                  <Icon name="trash" size={16} />{L('Obriši', 'Delete', 'Удалить')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <section style={styles.mHero} className="glow-card">
        <span style={styles.mClient}>{customer?.name || invoice.client || '—'}</span>
        <span style={styles.mAmount} className="mono">{money(total)}</span>
        {kind !== 'profaktura' && <span style={styles.mStatus}><StatusBadge status={invoice.status} /></span>}
        <div style={styles.mHeroBtns}>
          {canPay && (
            <button onClick={() => openDialog('payment')} style={styles.mPrimary}>
              <Icon name="checkCircle" size={18} />{L('Unesi uplatu', 'Record payment', 'Внести оплату')}
            </button>
          )}
          <button onClick={() => window.print()} style={styles.mSecondary}>
            <Icon name="download" size={18} />PDF
          </button>
        </div>
      </section>

      <section style={styles.mCard}>
        {infoRows.map(([k, v], i) => (
          <div key={k} style={{ ...styles.mInfoRow, ...(i === infoRows.length - 1 ? { borderBottom: 'none' } : {}) }}>
            <span style={styles.mInfoKey}>{k}</span>
            <span style={styles.mInfoVal}>{v}</span>
          </div>
        ))}
      </section>

      <section style={{ ...styles.mCard, padding: '14px 16px' }}>
        <div style={styles.mGroup}>{L('Stavke', 'Items', 'Позиции')}</div>
        {items.map((it, i) => (
          <div key={i} style={{ ...styles.mItem, ...(i === items.length - 1 ? { borderBottom: 'none', paddingBottom: 0 } : {}) }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={styles.mItemName}>{it.name || '—'}</span>
              <span style={styles.mItemSub}>
                {it.qty.toLocaleString('sr-Latn-RS')} {unitIn(it.unit, dl)} × {num2(it.price)}{it.discount ? ` · −${it.discount}%` : ''}
              </span>
            </span>
            <span style={styles.mItemTotal}>{money(it.total)}</span>
          </div>
        ))}
      </section>

      {invoice.note && (
        <section style={{ ...styles.mCard, padding: '14px 16px' }}>
          <div style={styles.mGroup}>{L('Poruka', 'Message', 'Сообщение')}</div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{invoice.note}</div>
        </section>
      )}

      <button onClick={() => { setShowDoc(true); window.scrollTo(0, 0); }} style={styles.mShowDoc}>
        <Icon name="fileText" size={18} />{L('Prikaži fakturu kako je klijent vidi', 'Show the invoice as the client sees it', 'Показать счёт как его видит клиент')}
      </button>
    </div>
  );

  return (
    <>
      {mobileEl}
      <style>{`
        @media (min-width: 761px) { .ipv-mobile { display: none !important; } }
        @media (max-width: 760px) { .ipv-desktop { display: none !important; } }
        /* "Kako je klijent vidi": bez statusa (Poslato/Naplaćeno) i ostalih oznaka samo za aplikaciju */
        .ipv-client-view .no-print-doc { display: none !important; }
      `}</style>
      <div className="ipv-desktop">
      <div className="no-print" style={styles.bar}>
        <button onClick={() => navigate('/fakturisanje')} style={styles.backBtn}>
          <Icon name="arrowLeft" size={16} />
          <span style={styles.barTitle}>{invoice.invoice_number || DOC_TITLES[kind]}</span>
        </button>
        <div style={styles.barBtns}>
          {kind === 'profaktura' && !linkedInvoice && (
            <Btn icon="receipt" label={L('Napravi fakturu', 'Create invoice', 'Создать счёт')} onClick={() => navigate(`/fakturisanje/nova?iz-profakture=${id}`)} variant="primary" />
          )}
          {kind !== 'profaktura' && st !== 'stornirano' && <Btn icon="x" label={L('Storniraj', 'Cancel invoice')} onClick={() => openDialog('storno')} variant="danger" />}
          <Btn icon="fileText" label={L('Napravi kopiju', 'Duplicate')} onClick={() => navigate(`/fakturisanje/nova?kopija=${id}`)} />
          <Btn icon="download" label="PDF" onClick={() => window.print()} />
          <Btn icon="logout" label={L('Pošalji', 'Send', 'Отправить')} onClick={() => setSending(true)} />
          {(kind || 'faktura') === 'faktura' && !invoice.sef_invoice_id && st !== 'stornirano' && (
            <Btn icon="globe" label={L('eFaktura (SEF)', 'e-Invoice (SEF)', 'SEF')} onClick={() => setSefOpen(true)} />
          )}
          {kind !== 'profaktura' && st !== 'stornirano' && st !== 'naplaceno' && (
            <Btn icon="checkCircle" label={L('Unesi uplatu', 'Record payment')} onClick={() => openDialog('payment')} variant="primary" />
          )}
          <Btn icon="pencil" label={L('Izmeni', 'Edit')} onClick={() => navigate(`/fakturisanje/${id}/izmeni`)} variant="primary" />
          <button onClick={() => openDialog('delete1')} style={styles.iconOnly} title={L('Obriši', 'Delete')} aria-label={L('Obriši', 'Delete')}>
            <Icon name="trash" size={16} />
          </button>
        </div>
      </div>

      {invoice.sent_at && (
        <div className="no-print" style={styles.sentNote}>
          {L('Poslato mejlom', 'Sent by e-mail', 'Отправлено')} {fmtDate(String(invoice.sent_at).slice(0, 10))} · {invoice.sent_to}
        </div>
      )}
      {kind === 'profaktura' && linkedInvoice && (
        <div className="no-print" style={styles.sentNote}>
          {L('Fakturisano', 'Invoiced', 'Выставлен счёт')} ·{' '}
          <button type="button" onClick={() => navigate(`/fakturisanje/${linkedInvoice.id}/pregled`)} style={styles.noteLink}>
            {L('faktura', 'invoice', 'счёт')} {linkedInvoice.invoice_number || ''}
          </button>
        </div>
      )}
      {kind !== 'profaktura' && invoice.source_proforma_id && (
        <div className="no-print" style={styles.sentNote}>
          <button type="button" onClick={() => navigate(`/fakturisanje/${invoice.source_proforma_id}/pregled`)} style={styles.noteLink}>
            {L('Nastala od profakture', 'Created from a proforma', 'Создан по проформе')}
          </button>
        </div>
      )}
      {invoice.sef_invoice_id && (
        <div className="no-print" style={styles.sentNote}>
          {L('eFaktura (SEF)', 'e-Invoice (SEF)', 'SEF')}{invoice.sef_env === 'demo' ? ' · demo' : ''} · {L('status', 'status', 'статус')}: <strong>{sefLabel(invoice.sef_status)}</strong>
          {invoice.sef_sent_at ? ` · ${fmtDate(String(invoice.sef_sent_at).slice(0, 10))}` : ''}
        </div>
      )}
      {docEl}
      </div>
      {createPortal(<div id="invoice-print-portal" className={IOS_PRINT ? 'print-flow' : undefined}>{docEl}</div>, document.body)}

      {/* Skriveni QR (kao slika) za PDF prilog */}
      {qrValue && (
        <div style={{ display: 'none' }} aria-hidden="true">
          <QRCodeCanvas id="ips-qr-canvas" value={qrValue} size={480} level="M" />
        </div>
      )}

      {sefOpen && invoice && (
        <SefSendDialog invoice={invoice} customerName={customer?.name} onClose={() => setSefOpen(false)} onSent={(d) => setInvoice(d)} />
      )}

      {sending && (
        <SendInvoiceDialog
          invoice={invoice} issuer={issuer} customer={customer} buildPdf={buildPdf} dl={dl}
          onClose={() => setSending(false)}
          onSent={(d) => setInvoice(d)}
        />
      )}

      {/* Unos uplate: isti prozor kao u listi faktura (iznos, datum, način, beleška) */}
      {dialog === 'payment' && (
        <PaymentDialog invoice={invoice} onClose={() => setDialog(null)} onSaved={(d) => { setInvoice(d); setDialog(null); }} />
      )}

      {dialog && dialog !== 'payment' && (
        <div className="no-print" style={styles.overlay} onClick={closeDialog}>
          <div style={styles.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {dialog === 'payment' && (
              <>
                <div style={styles.modalTitle}>{L('Unesi uplatu', 'Record payment')}</div>
                <div style={styles.modalText}>
                  {L('Preostalo za uplatu', 'Remaining')}: <strong>{money(due)}</strong>
                </div>
                <label style={styles.modalLabel}>{L(`Iznos uplate (${cur})`, `Amount (${cur})`, `Сумма оплаты (${cur})`)}</label>
                <input
                  autoFocus
                  type="number"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') doPayment(); }}
                  style={styles.modalInput}
                />
                <div style={styles.modalHint}>{L('Ako je uplaćen ceo iznos, faktura postaje "Naplaćeno", inače "Delimično".', 'Full amount marks it Paid, otherwise Partial.')}</div>
              </>
            )}
            {dialog === 'storno' && (
              <>
                <div style={styles.modalTitle}>{L('Storniraj dokument?', 'Cancel this invoice?')}</div>
                <div style={styles.modalText}>{L('Stornirana faktura ostaje u evidenciji, ali se više ne računa u promet, limite i KPO knjigu.', 'A cancelled invoice stays on record but no longer counts toward turnover or limits.')}</div>
              </>
            )}
            {dialog === 'delete1' && (
              <div style={styles.modalText}>{L(`Da li želite da obrišete dokument ${invoice.invoice_number || ''}? Nestaće i iz KPO knjige i iz limita.`, 'Delete this document? It will also be removed from the KPO ledger and limits.', `Удалить документ ${invoice.invoice_number || ''}? Он также исчезнет из книги KPO и лимитов.`)}</div>
            )}
            {dialog === 'delete2' && (
              <div style={{ ...styles.modalText, color: 'var(--red-600)', fontWeight: 700 }}>{L('Poslednja provera: brisanje je trajno i ne može se poništiti.', 'Final check: deletion is permanent.')}</div>
            )}
            {dialogError && <div style={styles.modalError}>{dialogError}</div>}
            <div style={styles.modalRow}>
              <button onClick={closeDialog} disabled={busy} style={styles.cancelBtn}>{L('Otkaži', 'Cancel')}</button>
              {dialog === 'payment' && <button onClick={doPayment} disabled={busy} style={styles.confirmBtn}>{busy ? '...' : L('Sačuvaj uplatu', 'Save payment')}</button>}
              {dialog === 'storno' && <button onClick={doStorno} disabled={busy} style={styles.dangerBtn}>{busy ? '...' : L('Da, storniraj', 'Yes, cancel it')}</button>}
              {dialog === 'delete1' && <button onClick={() => setDialog('delete2')} style={styles.confirmBtn}>{L('Nastavi', 'Continue')}</button>}
              {dialog === 'delete2' && <button onClick={doDelete} disabled={busy} style={styles.dangerBtn}>{busy ? '...' : L('Da, trajno obriši', 'Yes, delete')}</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  // ---------- telefon ----------
  mHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  mRound: { width: 44, height: 44, flexShrink: 0, border: 'none', borderRadius: '50%', background: 'var(--paper)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  mTitle: { flex: 1, minWidth: 0, fontSize: 17, fontWeight: 800, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mPdfSmall: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 40, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--teal-800)', borderRadius: 12, padding: '0 14px', fontWeight: 700, fontSize: 14 },
  mBackdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  mMenu: { position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, width: 220, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, boxShadow: 'var(--shadow-lg)', padding: 6 },
  mMenuItem: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, border: 'none', background: 'transparent', borderRadius: 10, padding: '0 10px', fontSize: 15, fontWeight: 600, color: 'var(--ink-900)', textAlign: 'left', cursor: 'pointer' },
  mHero: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '20px 16px 16px', marginBottom: 12 },
  mClient: { fontSize: 14, fontWeight: 600, color: 'var(--ink-700)' },
  mAmount: { fontSize: 30, fontWeight: 800, color: 'var(--ink-900)', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' },
  mStatus: { display: 'inline-flex', background: 'var(--paper)', borderRadius: 7 },
  mHeroBtns: { display: 'flex', gap: 10, width: '100%', marginTop: 12 },
  sentNote: { maxWidth: 900, margin: '-6px auto 12px', fontSize: 12.5, color: 'var(--forest-600)', fontWeight: 600 },
  noteLink: { background: 'none', border: 'none', padding: 0, color: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' },
  mPrimary: { flex: 1, minHeight: 48, border: 'none', borderRadius: 14, background: 'var(--teal-800)', color: '#fff', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' },
  mSecondary: { flex: 1, minHeight: 48, border: '1px solid var(--line)', borderRadius: 14, background: '#fff', color: 'var(--teal-800)', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' },
  mCard: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: '2px 16px', marginBottom: 12 },
  mInfoRow: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--line)', fontSize: 14 },
  mInfoKey: { color: 'var(--ink-500)' },
  mInfoVal: { fontWeight: 700, textAlign: 'right', color: 'var(--ink-900)' },
  mGroup: { fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 8 },
  mItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '8px 0 10px', borderBottom: '1px solid var(--line)' },
  mItemName: { fontSize: 15, fontWeight: 600, color: 'var(--ink-900)' },
  mItemSub: { fontSize: 13, color: 'var(--ink-500)' },
  mItemTotal: { fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', color: 'var(--ink-900)' },
  mShowDoc: { width: '100%', minHeight: 52, border: '1px solid var(--line)', borderRadius: 16, background: 'var(--surface)', color: 'var(--teal-800)', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 },

  center: { padding: 40, textAlign: 'center', color: 'var(--ink-500)' },
  bar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: '12px 16px', maxWidth: 900, margin: '0 auto 16px' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', color: 'var(--ink-900)', padding: 0, cursor: 'pointer' },
  barTitle: { fontSize: 17, fontWeight: 800 },
  barBtns: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  btn: { display: 'flex', alignItems: 'center', gap: 7, minHeight: 40, borderRadius: 10, padding: '0 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  outline: { border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--teal-800)' },
  primary: { border: 'none', background: 'var(--teal-800)', color: '#fff' },
  danger: { border: 'none', background: 'var(--red-600)', color: '#fff' },
  iconOnly: { width: 40, height: 40, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--red-600)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  doc: {
    background: '#fff', maxWidth: 900, margin: '0 auto 40px', padding: '44px 48px', borderRadius: 16, border: '1px solid var(--line)',
    display: 'flex', flexDirection: 'column', minHeight: 1180, boxSizing: 'border-box',
    fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', sans-serif", fontSize: 13, color: '#17242b', lineHeight: 1.65,
  },
  topRow: { display: 'flex', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' },
  logo: { maxHeight: 60, maxWidth: 210, objectFit: 'contain', marginBottom: 10, display: 'block' },
  logoDrop: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, width: 'min(320px, 100%)', minHeight: 96, marginBottom: 14, border: '1.5px dashed var(--lavender-500)', background: 'var(--lavender-100)', borderRadius: 12, padding: 12, textAlign: 'center', fontSize: 12, color: 'var(--ink-900)', cursor: 'pointer' },
  issuerName: { fontSize: 14, fontWeight: 800, marginBottom: 2 },
  headerText: { fontSize: 12.5, lineHeight: 1.6 },
  metaBox: { minWidth: 260 },
  docTitle: { fontSize: 21, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 },
  docNumber: { fontSize: 15, fontWeight: 700, color: '#12343f' },
  metaTable: { borderCollapse: 'collapse' },
  metaLabel: { padding: '2px 18px 2px 0', color: '#4a5960', whiteSpace: 'nowrap', verticalAlign: 'top' },
  metaValue: { padding: '2px 0', fontWeight: 700 },
  divider: { height: 1, background: '#e7e6e1', margin: '20px 0' },
  blockLabel: { fontSize: 12, color: '#5f6b71', marginBottom: 8 },
  partyName: { fontSize: 15, fontWeight: 800, marginBottom: 2 },
  items: { width: '100%', borderCollapse: 'collapse', marginTop: 30 },
  th: { textAlign: 'left', padding: '12px 10px', borderTop: '1px solid #e7e6e1', borderBottom: '1px solid #e7e6e1', fontSize: 12, fontWeight: 700, color: '#17242b' },
  td: { padding: '14px 10px', borderBottom: '1px solid #e7e6e1', verticalAlign: 'top' },
  totalsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 28, flexWrap: 'wrap', marginTop: 18 },
  vatNote: { flex: '1 1 260px', maxWidth: 440, fontSize: 12.5, lineHeight: 1.55, color: '#17242b', paddingTop: 4 },
  totals: { marginLeft: 'auto', width: 'min(340px, 100%)' },
  signLabel: { fontSize: 13, color: '#17242b' },
  signName: { fontSize: 15, fontWeight: 700, color: '#17242b' },
  footer: { marginTop: 'auto', paddingTop: 14, borderTop: '1px solid #e7e6e1', textAlign: 'center', fontSize: 11.5, color: '#4a5960', lineHeight: 1.6 },
  totalRow: { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '4px 10px' },
  totalDue: { marginTop: 6, padding: '10px', background: '#f6f6f3', borderRadius: 8, fontWeight: 800, fontSize: 15 },
  stornoNote: { marginTop: 18, padding: '10px 14px', background: '#fdecec', color: '#a12828', borderRadius: 8, fontWeight: 700 },
  proformaNote: { marginTop: 18, padding: '10px 14px', background: '#f6eedc', color: '#8a5a0b', borderRadius: 8, fontSize: 12 },
  bottomRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap', marginTop: 30, marginBottom: 28, paddingTop: 20, borderTop: '1px solid #e7e6e1' },
  note: { margin: '0 0 10px', whiteSpace: 'pre-wrap', fontWeight: 600 },
  legal: { margin: '0 0 6px', fontSize: 11.5, color: '#4a5960' },
  qrBox: { textAlign: 'center' },
  qrLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', marginTop: 6 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(12,37,48,0.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: 'var(--surface)', borderRadius: 18, padding: 24, width: '100%', maxWidth: 420, boxShadow: 'var(--shadow-lg)' },
  modalTitle: { fontSize: 17, fontWeight: 800, marginBottom: 8 },
  modalText: { fontSize: 14, lineHeight: 1.5, color: 'var(--ink-900)', marginBottom: 14 },
  modalLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 6 },
  modalInput: { width: '100%', border: '1.5px solid var(--line)', borderRadius: 10, padding: '11px 13px', fontSize: 15, marginBottom: 8 },
  modalHint: { fontSize: 12, color: 'var(--ink-500)', marginBottom: 14 },
  modalError: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 12 },
  modalRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' },
  cancelBtn: { minHeight: 42, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '0 16px', fontWeight: 600, fontSize: 14, color: 'var(--ink-700)' },
  confirmBtn: { minHeight: 42, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 10, padding: '0 16px', fontWeight: 700, fontSize: 14 },
  dangerBtn: { minHeight: 42, border: 'none', background: 'var(--red-600)', color: '#fff', borderRadius: 10, padding: '0 16px', fontWeight: 700, fontSize: 14 },
};
